'use client'

import { assert } from '../../../../MyStichtingOpenHelpers/src/static'
import type { InputWithValidation } from '@stopen-helpers/static'
import React, { type FC, type ReactNode, useEffect, useRef } from 'react'

const submitForm = (form: HTMLFormElement | null) => {
    assert(!!form, 'no form could  be submitted')
    if (!form) return
    form.requestSubmit()
}

interface Props {
    children: ReactNode
}

/**
 * @description
 * Place around one or more input fields to submit their form on blur.
 * Only submits the form when the event's relatedTarget is undefined | null.
 * In other words, if you move out of one of these input fields, but move into another
 * input field that also belongs to the same form, then the form will not submit.
 *
 * Can be used with any form, regardles if it is managed by useForm
 *
 * Best practice for UX is to put this component around all the inputs
 * of a certain form.
 *
 * @listens 'focusout'
 * */
export const SubmitOnBlur: FC<Props> = ({ children }) => {
    const container = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!container.current) {
            return
        }

        const controller = new AbortController()
        const signal = controller.signal

        container.current.addEventListener(
            'focusout',
            (event: FocusEvent) => {
                if (!event.relatedTarget) submitForm((event.target as InputWithValidation)?.form)
            },
            { signal },
        )

        return () => {
            controller.abort()
        }
    }, [container])
    return (
        <div className="contents" ref={container}>
            {children}
        </div>
    )
}
